import React, { createContext, useContext, useState } from "react";
import {
  Snackbar,
  Alert,
  AlertColor,
  SnackbarCloseReason,
  useMediaQuery,
  useTheme,
} from "@mui/material";

interface AlertState {
  showAlert: boolean;
  alertMsg: string;
  alertType: AlertColor;
}

interface AlertContextType {
  alert: AlertState;
  setAlert: React.Dispatch<React.SetStateAction<AlertState>>;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const AlertProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const [alert, setAlert] = useState<AlertState>({
    showAlert: false,
    alertMsg: "",
    alertType: "info",
  });

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setAlert({
      showAlert: false,
      alertMsg: "",
      alertType: "info",
    });
  };

  return (
    <AlertContext.Provider value={{ alert, setAlert }}>
      {children}
      <Snackbar
        open={alert.showAlert}
        autoHideDuration={15000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ width: isLargeScreen ? theme.breakpoints.values.md : "90%" }}
      >
        <Alert
          onClose={handleClose}
          severity={alert.alertType}
          variant="filled"
        >
          {alert.alertMsg}
        </Alert>
      </Snackbar>
    </AlertContext.Provider>
  );
};

export const useAlert = (): AlertContextType => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error("useAlert must be used within an AlertProvider");
  }
  return context;
};
